import { TOKEN, cryptData, decryptData } from '../utils';
import axios, { AxiosHeaderValue, AxiosHeaders, AxiosRequestConfig, AxiosResponseHeaders, Method } from 'axios';

import { _msal } from '../msal/AdHelper';
import { loginRequest } from '../msal/azureAd';

export interface IRequest<T = any> {
    url: string;
    baseURL: string;
    data?: T;
}

export interface IResponse<D = any> {
    data: D;
    status: number;
    statusText: string;
    headers: TResponseHeaders;
    config: IRequestConfig;
    request?: any;
}

export type TRequestHeaders = Record<string, AxiosHeaderValue | AxiosHeaders>;

interface IRequestConfig extends AxiosRequestConfig {}

type TResponseHeaders = AxiosResponseHeaders;

type TMethod = Method;

const httpClient = () => {
    const URL = process.env.REACT_APP_API_URL;

    const _instance = axios.create({
        timeout: parseInt(process.env.TIMEOUT_HTTPCLIENT as string),
    });

    _instance.defaults.headers.common['Authorization'] = 'Bearer ' + localStorage.getItem(TOKEN);

    _instance.interceptors.response.use(
        res => res,
        async error => {
            console.log(error);
            // Do something with response error
            const originalRequest = error.config;
            const status = error.response?.status;

            if ((status === 401 || status === 403) && originalRequest && !originalRequest._retry) {
                originalRequest._retry = true;

                const account = _msal.getActiveAccount();
                if (!account) {
                    await _msal.loginRedirect();
                    return Promise.reject(error);
                }

                try {
                    const access_token = await _msal.acquireTokenSilent({ account, ...loginRequest });
                    localStorage.setItem(TOKEN, access_token.accessToken);

                    // Update the default header for subsequent requests...
                    _instance.defaults.headers.common['Authorization'] = 'Bearer ' + access_token.accessToken;

                    // ...and the original request headers, otherwise the retry would
                    // still carry the stale bearer set at request-build time.
                    originalRequest.headers = originalRequest.headers ?? {};
                    originalRequest.headers['Authorization'] = 'Bearer ' + access_token.accessToken;

                    return _instance(originalRequest);
                } catch (refreshError) {
                    // Silent refresh failed (e.g. interaction required / refresh token
                    // expired): fall back to an interactive login instead of failing silently.
                    await _msal.loginRedirect();
                    return Promise.reject(refreshError);
                }
            }

            return Promise.reject(error);
        },
    );

    const fetcher = (url: string) => _instance.get(URL + url, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN)}` } }).then(res => res.data);

    const postRequest = (url: string, { arg }: { arg: any }) =>
        _instance.post(URL + url, arg, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN)}`, 'Content-Type': 'application/json' } }).then(res => res.data);

    const putRequest = (url: string, { arg }: { arg: any }) =>
        _instance.put(URL + url, arg, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN)}`, 'Content-Type': 'application/json' } }).then(res => res.data);
    return {
        fetcher,
        postRequest,
        putRequest,
    };
};

export default httpClient();
